import { createApi } from '@reduxjs/toolkit/dist/query/react'
import { getSuggestsQuery } from 'feature/DaData/getSuggestsQuery'
import { orderApiSpace } from 'feature/productOrder/namespaces/orderApiSpace'
import { TSuggest } from 'feature/registration/types'
import { getBaseQueryAuth } from 'helpers/rtkQueryHelpers'

import { orderFormSpace } from '../namespaces/orderFormSpace'

// API для работы с заявками
export const ordersApi = createApi({
  reducerPath: 'orders',
  baseQuery: getBaseQueryAuth,
  tagTypes: [
    'productOrderNewType',
    'productOrderServiceType',
    'productOrderParam',
  ],
  endpoints: builder => ({
    // продукты для нового заказа
    getOrderNewProdType: builder.query<orderApiSpace.ProductTypeList, void>({
      query: () => ({
        url: '/v1/product/type/order',
        method: 'GET',
      }),
      providesTags: ['productOrderNewType'],
    }),
    // продукты для серверного обслуживания
    getOrderServiceProdType: builder.query<orderApiSpace.ProductTypeList, void>(
      {
        query: () => ({
          url: '/v1/product/type/service-maintenance',
          method: 'GET',
        }),
        providesTags: ['productOrderServiceType'],
      },
    ),
    // продукты в зависимости от типа формы
    getOrderProdTypeByForm: builder.query<
      orderApiSpace.ProductTypeList,
      orderFormSpace.FormType
    >({
      queryFn: async (formType, { dispatch }) => {
        let products: orderApiSpace.ProductTypeList
        switch (formType) {
          case orderFormSpace.FormType.new:
          case orderFormSpace.FormType.newCompany:
            products = (await dispatch(
              ordersApi.endpoints.getOrderNewProdType.initiate(),
            ).unwrap()) as orderApiSpace.ProductTypeList
            break
          case orderFormSpace.FormType.service:
            products = (await dispatch(
              ordersApi.endpoints.getOrderServiceProdType.initiate(),
            ).unwrap()) as orderApiSpace.ProductTypeList
            break
          default:
            throw new Error('Неизвестная форма!')
        }
        return { data: products }
      },
      providesTags: (result, error, formType) => {
        switch (formType) {
          case orderFormSpace.FormType.new:
          case orderFormSpace.FormType.newCompany:
            return [{ type: 'productOrderNewType' }]
          case orderFormSpace.FormType.service:
            return [{ type: 'productOrderServiceType' }]
          default:
            throw new Error('Неизвестная форма!')
        }
      },
    }),
    // получения списка параметров для массива продуктов
    getProdParamsByTypeIds: builder.query<
      orderApiSpace.ProductParamList,
      orderFormSpace.Product[]
    >({
      query: products => ({
        url: '/v1/product/list/parameters',
        method: 'POST',
        body: { productIds: products.map(item => item.id) },
      }),
      transformResponse: (
        res: orderApiSpace.ProductParamList,
        unknown,
        args,
      ) => {
        const modifiedResponse = {
          list: args.map(item => ({
            productId: item.id,
            productName: item.name,
            parameters:
              res.list.find(cur => cur.productId === item.id)?.parameters ||
              null,
          })),
        }
        return modifiedResponse
      },
      providesTags: ['productOrderParam'],
    }),
    // загрузка изображения для заявки
    uploadImage: builder.mutation<orderApiSpace.UploadImage, File>({
      query(file) {
        const bodyFormData = new FormData()
        bodyFormData.append('file', file)
        return {
          url: '/v1/order/image',
          method: 'POST',
          body: bodyFormData,
          formData: true,
        }
      },
    }),
    // получения списка тайм слотов
    getTimeSlots: builder.query<orderApiSpace.TimeSlotList, string>({
      query: fiasId => ({
        url: `/v1/region/time-slot/${fiasId}/list`,
        method: 'GET',
      }),
    }),
    // получения списка тайм слотов после после адресса (идем в дадату)
    getTimeSlotsAfterAddress: builder.query<orderApiSpace.TimeSlotList, string>(
      {
        queryFn: async (query, { dispatch }) => {
          try {
            const response = await getSuggestsQuery(query)
            const addressList: TSuggest[] = response.data

            if (!addressList || addressList.length === 0) {
              throw new Error('Ошибка валидации адреса с Дадатой')
            }

            const regionFiasId = addressList[0].regionFiasId
            if (!regionFiasId) {
              throw new Error('В адресе отсутствует regionFiasId')
            }

            const timeSlots: orderApiSpace.TimeSlotList = (await dispatch(
              ordersApi.endpoints.getTimeSlots.initiate(regionFiasId),
            ).unwrap()) as orderApiSpace.TimeSlotList

            return { data: timeSlots }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            return {
              error: error.message,
            }
          }
        },
      },
    ),
    // отправить форму для "заказа изделия", "заказа на сервис"
    // TODO описать тип
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sendOrderForm: builder.mutation<{ uid: string }, any>({
      query: form => ({
        url: '/v1/order',
        body: form,
        method: 'POST',
      }),
    }),
    // проверка валидности кода дилера
    checkDealerCode: builder.query<orderApiSpace.CheckingDealer, string>({
      query: code => ({
        url: `/v1/info/check-dealer-code`,
        method: 'POST',
        body: { dealerCode: code },
      }),
    }),
    // проверка территории на обслуживаемость
    checkTerritory: builder.query<
      orderApiSpace.CheckingAddress,
      orderApiSpace.CheckingAddressRequestBody
    >({
      query: address => ({
        url:
          `/v1/info/address-kladr-in-served-territory?` +
          `regionKladrId=${address.regionKladrId}&` +
          `cityKladrId=${address.cityKladrId}&areaKladrId=${address.areaKladrId}`,
        method: 'GET',
      }),
    }),
    uploadConsultationImage: builder.mutation<orderApiSpace.UploadImage, File>({
      query(file) {
        const bodyFormData = new FormData()
        bodyFormData.append('file', file)
        return {
          url: '/v1/order/create/image/consultation-before-ordering',
          method: 'POST',
          body: bodyFormData,
          formData: true,
        }
      },
    }),
    sendConsultForm: builder.mutation<
      { uid: string },
      {
        data: {
          comment: string
          fileList?: string[]
          productTypeList?: number[]
        }
        type: orderFormSpace.ConsultationFormType
      }
    >({
      query: ({ data: form, type: currentEdit }) => {
        if (currentEdit === orderFormSpace.ConsultationFormType.consultation) {
          return {
            url: '/v1/order/create/consultation-before-ordering',
            body: form,
            method: 'POST',
          }
        } else {
          return {
            url: '/v1/order/create/consultation-before-service',
            body: form,
            method: 'POST',
          }
        }
      },
    }),
    getConsultProductList: builder.query<orderApiSpace.ProductTypeList, void>({
      query: () => ({
        url: `/v1/product/type/consultations`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetOrderProdTypeByFormQuery,
  useGetProdParamsByTypeIdsQuery,
  useUploadImageMutation,
  useGetTimeSlotsQuery,
  useGetTimeSlotsAfterAddressQuery,
  useSendOrderFormMutation,
  useCheckDealerCodeQuery,
  useLazyCheckDealerCodeQuery,
  useLazyCheckTerritoryQuery,
  useUploadConsultationImageMutation,
  useSendConsultFormMutation,
  useGetConsultProductListQuery,
} = ordersApi
